import { DateTime, TimeZone } from 'timezonecomplete';

import type { IWeekDay } from '@/types/calendar';
import type { ISortedShift } from '@/types/schedule';
import type { HcColor } from '@/components/client/scheduling/types';

import { CalendarDisplayRange, WeekDays } from '@/store/enums';
import { checkCollision } from '@/helpers';
import { CALENDAR_MESSAGE_HEIGHT, CALENDAR_MESSAGE_HEIGHT_MOBILE, DAYS_IN_WEEK } from '@/config/variables';
import { LabourFull, LabourUnassigned, LabourUnderstaffed, LabourVto } from '@/assets/icons';
import { colors } from '@/styles/colors';

/**
 * Calculates calendar's message box height
 * @param isCalendarMessageDisplayed - is a message displayed on the calendar
 * @param mobile - is it a mobile size website
 */
const getCalendarMessageHeight = (isCalendarMessageDisplayed: boolean | undefined, mobile?: boolean): number => {
  if (mobile) return isCalendarMessageDisplayed ? CALENDAR_MESSAGE_HEIGHT_MOBILE : 0;
  return isCalendarMessageDisplayed ? CALENDAR_MESSAGE_HEIGHT : 0;
};

/**
 * Provides a functionality to manage calendar view days
 * @param weekDays - all days for given week
 * @param displayDays - display range for current view, e.g. one day, three days or whole week
 * @param offset - offset from the start of the given week. 0 === Monday, 6 === Sunday
 * @param timezoneName - user's timezone
 * @returns IWeekDay[] of days to be displayed on the calendar
 */
const getWeekDaysForCalendar = (
  weekDays: IWeekDay[],
  displayDays: CalendarDisplayRange,
  offset: WeekDays | null,
  timeZone: string,
): IWeekDay[] => {
  const indexCurrentDay = weekDays.findIndex(item => item.isCurrentDay); // Does "weekDays" include a currentDay item

  // gets week day index with correction to start a week on monday with 0 index
  const indexToday = (DateTime.now(TimeZone.zone(timeZone)).weekDay() + 6) % 7;

  switch (displayDays) {
    case CalendarDisplayRange.ONE_DAY: {
      return offset === null || offset < 0 || offset > DAYS_IN_WEEK
        ? indexCurrentDay !== -1
          ? weekDays.filter(item => item.isCurrentDay)
          : weekDays.slice(indexToday, indexToday + 1)
        : weekDays.slice(offset, offset + 1);
    }
    case CalendarDisplayRange.THREE_DAYS: {
      /*
        HANDLING CALENDAR VIEW FOR 3 DAYS (3-items array).
        The today (current day) is displayed always in the middle (as a 2nd day of three).

        For parsing weekDays the Array.slice(start, end) is used. Setting start/end params
        specifically to match [previousDay, TODAY, nextDay].
      */

      // baseIndex - the index of "today" (the current day) or index provided by the user
      // offset - the start index provided by the user (kept in a slug)
      // Current day is highlighted in calendar when it's included in a selected week.
      const baseIndex =
        offset === null || offset < 0 || offset > 7 ? (indexCurrentDay !== -1 ? indexCurrentDay : indexToday) : offset;
      let startIndex = WeekDays.Monday;
      let endIndex: number | undefined = WeekDays.Sunday;

      // IF statements below are only used to set startIndex, endIndex based on baseIndex
      if (baseIndex < WeekDays.Saturday) {
        startIndex = baseIndex;
        endIndex = baseIndex + 3;
      }
      if (baseIndex === WeekDays.Saturday) {
        startIndex = baseIndex - 1;
        endIndex = undefined;
      }
      if (baseIndex === WeekDays.Sunday) {
        startIndex = baseIndex - 2;
        endIndex = undefined;
      }

      return weekDays.slice(startIndex, endIndex);
    }
    case CalendarDisplayRange.WEEK:
      return weekDays;
  }
};

/**
 * Calculates columns of given shift block
 * @param shift - shift block in calendar
 * @param columnIndex - index for currently calculated shift block
 * @param newRow - row of given shift block
 */
const getColumnSpan = (shift: ISortedShift, columnIndex: number, newRow: ISortedShift[][]): number => {
  let colSpan = 1;
  if (columnIndex === newRow.length - 1) {
    return colSpan;
  }

  for (let x = columnIndex + 1; x < newRow.length; x++) {
    const nextColumn = newRow[columnIndex + colSpan];
    if (!nextColumn || nextColumn.some(item => checkCollision(shift.displayBlocks, item.displayBlocks))) {
      break;
    }

    colSpan++;
  }

  return colSpan;
};

/**
 * Maps the headcount color from the API to match a color scheme
 * @param hcColor - enum that indicates state of shifts - ratio of signed SPs to expected SPs
 */
const hcColorToHex = (hcColor: HcColor): string => {
  switch (hcColor) {
    case 'empty':
      return colors.red.c500;
    case 'underfill':
      return colors.orange.c500;
    case 'full':
      return colors.green.c500;
    case 'overflow':
      return colors.purple.c500;
    default:
      return colors.blue.c500;
  }
};

/**
 * Maps the headcount color from the API to match an icon scheme
 * @param hcColor - enum that indicates state of shifts - ratio of signed SPs to expected SPs
 */
const hcColorToIcon = (hcColor: HcColor): string => {
  switch (hcColor) {
    case 'empty':
      return LabourUnassigned;
    case 'underfill':
      return LabourUnderstaffed;
    case 'full':
      return LabourFull;
    case 'overflow':
      return LabourVto;
  }
};

export { getWeekDaysForCalendar, getColumnSpan, hcColorToHex, hcColorToIcon, getCalendarMessageHeight };
